import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import ReactAutosuggest from 'react-autosuggest'
import queryString from 'query-string'
import styled from 'styled-components'
import { withRouter } from 'react-router-dom'
import { actionCreators as searchInputActionCreators } from 'reduxBits/searchInput'
import { IconSearch, IconClose, IconButton } from 'blocket-ui'
import Suggestion from './SearchSuggestion'

const CHANGE_METHOD = {
  TEXT: 'text',
  SUGGESTION: 'suggestion',
  HISTORY: 'history',
}

const getSuggestionValue = (suggestion) => suggestion.q

const useAutoSuggestions = (search, searchInputActions) => {
  const handleSuggestionsFetchRequested = useCallback(
    ({ value: newValue }) => {
      searchInputActions.fetchCompletions({
        ...queryString.parse(search),
        q: newValue,
      })
    },
    [search, searchInputActions],
  )
  const handleSuggestionsClearRequested = useCallback(() => {
    searchInputActions.clearCompletions()
  }, [searchInputActions])

  return [handleSuggestionsFetchRequested, handleSuggestionsClearRequested]
}

export const StyledWrapper = styled.div`
  display: flex;
  position: relative;
  flex-grow: 1;
  margin-bottom: 8px;
  margin-top: 4px;

  &.typed input {
    padding-right: 50px;
    padding-left: 40px;
    width: 100%;

    &::placeholder {
      text-indent: 0;
    }
  }
  &.typed input[value] {
    text-indent: 0 !important;
  }

  &.search-header {
    &.typed input {
      padding-right: 110px;
    }
  }

  & .react-autosuggest__container {
    position: relative;
    flex-grow: 1;
    display: flex;
  }

  & .react-autosuggest__input {
    font-family: 'BlocketSans-Regular';
    -webkit-font-smoothing: antialiased;
    font-size: 17px;
    -webkit-appearance: none;
    outline: 0;
    padding-left: 40px;
    flex-grow: 1;
    color: #363434;
    border: 1px solid #9a9999;
    border-radius: 4px;
    background-color: white;
    cursor: text;
    height: 48px;

    & :focus {
      & + div {
        display: block;
      }
    }

    &::placeholder {
      color: #9a9999;
    }
  }

  & .react-autosuggest__suggestions-container {
    z-index: 1000;
  }

  & .react-autosuggest__suggestions-container--open {
    position: absolute;
    border: 1px solid #9a9999;
    border-top-color: #efefef;
    border-top-width: 0.5px;
    border-radius: 0 0 6px 6px;
    top: 45px;
    left: 0;
    right: 0;
    overflow: hidden;
    background-color: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    z-index: 1000;

    & .react-autosuggest__suggestions-list li:first-child > div {
      padding-top: 12px;
    }

    & .react-autosuggest__suggestions-list li:last-child > div {
      padding-bottom: 12px;
    }
  }
`

export const StyledSearchIcon = styled(IconSearch)`
  position: absolute;
  padding: 18px 0 13px 15px;
  pointer-events: none;
  z-index: 1010;
`

export const StyledCloseIcon = styled(IconClose)`` // Needed to be able to style on hover. See line 131

export const StyledClearButton = styled(IconButton)`
  position: absolute;
  right: 1px;
  width: 48px;
  height: 100%;
  background-color: transparent;
  top: 50%;
  transform: translate(0%, -50%);
  z-index: 1015;
  cursor: pointer;
  padding: 5px;
  &:hover ${StyledCloseIcon} {
    color: #000;
  }
`

const icon = <StyledCloseIcon width={24} height={24} />

const shouldRenderSuggestions = () => true

export const SearchInput = ({
  onChange,
  onReset,
  initialValue = '',
  suggestions,
  placeholder = 'Vad vill du söka efter?',
  renderInputComponent,
  location,
  searchInputActions,
  historyEntries,
  className,
}) => {
  const [searchValue, setSearchValue] = useState(initialValue)
  const [userQuery, setUserQuery] = useState(initialValue)
  const [inputFocus, setInputFocus] = useState(false)
  const dispatch = useDispatch()
  let searchInputRef = null

  const currentSearchParams = queryString.parse(location.search)

  useEffect(() => {
    searchInputActions.getHistory()
  }, [searchInputActions])

  const suggestionsOrHistory =
    (userQuery.length && suggestions.length && suggestions) ||
    (!userQuery.length && historyEntries) ||
    []

  const handleInputChange = useCallback(
    (event, { newValue, method }) => {
      // We only set the user query if the user is typing
      // otherwise it's changing when the user
      // selects with the arrow keys
      if (method === 'type') {
        setSearchValue(newValue)
        setUserQuery(newValue)
      } else {
        setSearchValue(newValue)
      }

      onChange({ q: newValue }, CHANGE_METHOD.TEXT)
    },
    [setSearchValue, setUserQuery, onChange],
  )

  const handleSuggestionSelect = useCallback(
    (event, { q, history, cg, cgLabel }) => {
      event.preventDefault()

      setSearchValue(q)
      setUserQuery(q)

      dispatch(searchInputActionCreators.updateHistory(q, { cg, cgLabel, history }))
      const newSearchParams = { q, ...(cg && { cg }) }
      if (cg && cg === currentSearchParams.cg) {
        delete newSearchParams.cg
      }
      const suggestionParams = { cgLabel, history }
      const changeMethod = history ? CHANGE_METHOD.HISTORY : CHANGE_METHOD.SUGGESTION
      onChange(newSearchParams, changeMethod, suggestionParams)
    },
    [onChange, setSearchValue, setUserQuery, dispatch, currentSearchParams.cg],
  )

  const renderSuggestion = useCallback(
    (suggestion, { isHighlighted }) => {
      const handleRemoveHistoryEntry = (e, q, cg) => {
        // prevent selection of the clicked suggestion
        e.stopPropagation()
        e.preventDefault()
        dispatch(searchInputActionCreators.removeFromHistory(q, cg))
      }
      return (
        <Suggestion
          key={suggestion.q + suggestion.cgLabel}
          suggestion={suggestion}
          userQuery={userQuery}
          isHighlighted={isHighlighted}
          handleRemove={handleRemoveHistoryEntry}
        />
      )
    },
    [userQuery, dispatch],
  )

  const handleSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      searchInputActions.chooseSuggestion(suggestion)

      handleSuggestionSelect(event, suggestion)
    },
    [searchInputActions, handleSuggestionSelect],
  )

  const _onReset = (event) => {
    setSearchValue('')
    setUserQuery('')

    searchInputActions.clearCompletions()

    if (searchInputRef !== null) {
      searchInputRef.focus()
      event.preventDefault()
    }

    if (onReset) {
      onReset()
    }
  }

  const autosuggestInputProps = useMemo(
    () => ({
      value: searchValue,
      onChange: handleInputChange,
      placeholder,
      'aria-label': placeholder,
    }),
    [searchValue, handleInputChange, placeholder],
  )

  const [handleSuggestionsFetchRequested, handleSuggestionsClearRequested] = useAutoSuggestions(
    location.search,
    searchInputActions,
  )

  const onFocus = () => setInputFocus(true)

  const onBlur = () => setInputFocus(false)

  if (userQuery.length > 0 && inputFocus) {
    className += ' typed'
  }

  return (
    <StyledWrapper className={className} onFocus={onFocus} onBlur={onBlur}>
      <StyledSearchIcon height={16} width={16} color="#696766" />
      <ReactAutosuggest
        id="search-input"
        suggestions={suggestionsOrHistory}
        shouldRenderSuggestions={shouldRenderSuggestions}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        inputProps={autosuggestInputProps}
        ref={(autosuggest) => {
          if (autosuggest !== null) {
            searchInputRef = autosuggest.input
          }
        }}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={handleSuggestionSelected}
      />
      {userQuery.length > 0 && inputFocus && (
        <StyledClearButton
          icon={icon}
          activeIcon={icon}
          className={inputFocus ? 'input-focus' : ''}
          onMouseDown={_onReset}
          onClick={() => {}}
        />
      )}
    </StyledWrapper>
  )
}

SearchInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  initialValue: PropTypes.string,
  className: PropTypes.string,

  placeholder: PropTypes.string,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  searchInputActions: PropTypes.object.isRequired,
  historyEntries: PropTypes.arrayOf(
    PropTypes.shape({ q: PropTypes.string, history: PropTypes.bool }),
  ),
  renderInputComponent: PropTypes.func,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      q: PropTypes.string,
      acFeedback: PropTypes.string,
      cgLabel: PropTypes.string,
      cg: PropTypes.string,
    }),
  ),
}

const mapStateToProps = (state) => ({
  suggestions: state.searchInput.completions,
  historyEntries: state.searchInput.history,
})

const mapDispatchToProps = (dispatch) => ({
  searchInputActions: bindActionCreators(searchInputActionCreators, dispatch),
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(SearchInput),
)
